import { useRef } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  Text,
  TextArea,
  RadioGroup,
  Datepicker,
  Checkbox,
  Select
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { Course } from 'shared-types';
import type { PageState } from 'types';

interface Props {
  course: Pick<
    Course,
    'certificateSendToUser' | 'id' | 'language' | 'languageVersions'
  >;
  includeNonParticipants?: boolean;
  isDropdown?: boolean;
  participant?: Pick<PageState['user'], 'email' | 'id' | 'username'> & {
    progressData?: Pick<PageState['user']['progressData'], 'completion'>;
  };
  updateUsers: () => Promise<void>;
}

const FollowUsersToggleComplete = ({
  course,
  includeNonParticipants = false,
  isDropdown,
  participant,
  updateUsers
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      courseCompletionAllowRemoval,
      requireReasonWhenDeletingData
    },
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const handleCompletionTimeChange = (date: number) => {
    if (!formApi.current) return;

    const currentStartDate = formApi.current.getValue(
      'startedAtDate'
    ) as number;
    if (currentStartDate > date) {
      formApi.current.setValue('startedAtDate', date);
    }
  };

  const handleSubmit = async (values: {
    comments: string;
    completionTime: string;
    deleteOptions: 'all' | 'answers' | 'completion';
    deletionReason?: string;
    inactivateOrDelete: 'delete' | 'inactivate';
    languageVersion: string;
    sendCertificate?: boolean;
    startedAtDate: string;
  }) => {
    let toggle: boolean;
    if (
      participant.progressData.completion &&
      participant.progressData.completion.status === 'success' &&
      !participant.progressData.completion.inactive
    ) {
      toggle = false;
    } else toggle = true;

    const response = await api.post(
      `/admin/courses/${course.id}/mark-complete`,
      {
        comments: values.comments ? values.comments : '',
        completionTime: values.completionTime || Date.now(),
        deleteOptions: values.deleteOptions,
        ...(values.deletionReason && { deletionReason: values.deletionReason }),
        inactivateOrDelete: values.inactivateOrDelete,
        includeNonParticipants,
        languageVersion: values.languageVersion || defaultLanguage,
        markComplete: toggle,
        participants: [
          {
            id: participant.id,
            email: participant.email,
            progressData: participant.progressData
          }
        ],
        requestUser: user.id,
        sendCertificate: values.sendCertificate || false,
        startedAtDate: values.startedAtDate
      }
    );

    await updateUsers();

    if (response.data.success) {
      dispatch.addNotice({
        text: messages.commonSavedSuccessfully,
        type: 'success'
      });
    }
  };

  let textFields: {
    isRemove: boolean;
    buttonIcon: string;
    buttonText: string;
    modalTitle: string;
  };

  if (
    participant.progressData.completion &&
    participant.progressData.completion.status === 'success' &&
    !participant.progressData.completion.inactive
  ) {
    textFields = {
      isRemove: true,
      buttonIcon: 'ban',
      buttonText: messages.completionManuallyMarkIncomplete,
      modalTitle: `${messages.completionManuallyRemoveFor} ${
        participant.email || participant.username
      }?`
    };
  } else {
    textFields = {
      isRemove: false,
      buttonIcon: 'check',
      buttonText: messages.completionManuallyMarkComplete,
      modalTitle: `${messages.completionManuallySetFor} ${
        participant.email || participant.username
      }?`
    };
  }

  const availableLanguageOptions = Object.entries(
    getCourseLanguageVersionOptions({ course })
  )
    .filter(([, lang]) => lang.isPublished)
    .map(([key, lang]) => ({ label: lang.capitalizedName, value: key }));

  const startedAtDate = participant.progressData?.completion?.startedAtDate;

  const completionLanguage =
    participant?.progressData?.completion?.courseLanguageVersion;

  const defaultLanguage =
    completionLanguage &&
    availableLanguageOptions.find((lang) => lang.value === completionLanguage)
      ? completionLanguage
      : course.language;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon={textFields.buttonIcon}
      buttonIsDropdown={isDropdown}
      buttonSize="header"
      buttonText={textFields.buttonText}
      buttonTextHideBreakpoint={breakpoints.small - 1}
      initialValues={{
        completionTime: dateNow,
        languageVersion: defaultLanguage,
        sendCertificate: course.certificateSendToUser,
        startedAtDate: startedAtDate
          ? Date.parse(startedAtDate as string)
          : dateNow
      }}
      modalActionText={messages.commonSubmit}
      modalAutoFocus={false}
      modalTitle={textFields.modalTitle}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi) => (formApi.current = form)}
      stopPropagation={true}
    >
      {({ values }) => (
        <>
          {textFields.isRemove ? (
            <>
              <FormGroup
                htmlFor={`deleteOptions-${participant.id}`}
                label={messages.completionManuallyRemoveOptions}
                maxWidth="100%"
              >
                <RadioGroup
                  id={`deleteOptions-${participant.id}`}
                  field="deleteOptions"
                  initialValue="completion"
                  options={[
                    {
                      id: `delete-only-completion-${participant.id}`,
                      label: messages.completionManuallyRemoveOnly,
                      value: 'completion'
                    },
                    {
                      id: `delete-answers-${participant.id}`,
                      label: messages.completionManuallyRemoveAnswers,
                      value: 'answers'
                    },
                    {
                      id: `delete-all-${participant.id}`,
                      label: messages.completionManuallyRemoveAll,
                      value: 'all'
                    }
                  ]}
                />
              </FormGroup>

              {courseCompletionAllowRemoval ? (
                <FormGroup
                  htmlFor={`completion-delete-options-${participant.id}`}
                  label={
                    messages.completionManuallyCompletionDeleteOrInactivate
                  }
                  maxWidth="100%"
                >
                  <RadioGroup
                    id={`completion-delete-options-${participant.id}`}
                    field="inactivateOrDelete"
                    initialValue="inactivate"
                    options={[
                      {
                        id: `inactivate-completion-${participant.id}`,
                        label: messages.completionManuallyCompletionInactivate,
                        tooltip:
                          messages.completionManuallyCompletionInactivateInfo,
                        value: 'inactivate'
                      },
                      {
                        id: `delete-completion-${participant.id}`,
                        label: messages.completionManuallyCompletionDelete,
                        value: 'delete'
                      }
                    ]}
                  />
                </FormGroup>
              ) : null}

              {requireReasonWhenDeletingData &&
              values.inactivateOrDelete === 'delete' ? (
                <FormGroup
                  htmlFor={`deletionReason-${participant.id}`}
                  label={messages.commonDeletionReason}
                  maxWidth="100%"
                >
                  <Text
                    field="deletionReason"
                    id={`deletionReason-${participant.id}`}
                    maxLength={255}
                    autocomplete="off"
                    placeholder={messages.commonInputDeletionReason}
                    required={true}
                  />
                </FormGroup>
              ) : null}
            </>
          ) : (
            <>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 20,
                  marginTop: 15
                }}
              >
                <FormGroup
                  htmlFor="completion-time"
                  label={messages.completionsCompletionDate}
                >
                  <Datepicker
                    field="completionTime"
                    id="completion-time"
                    onChange={handleCompletionTimeChange}
                    style={{ width: 160 }}
                  />
                </FormGroup>

                <FormGroup
                  htmlFor="default-started-at-date"
                  label={messages.completionManuallyStartedAtDate}
                  labelTooltip={messages.completionManuallyStartedAtDateTooltip}
                  style={{ marginTop: 0 }}
                >
                  <Datepicker
                    disabled={Boolean(startedAtDate)}
                    field="startedAtDate"
                    id="default-started-at-date"
                    maxDate={values.completionTime || dateNow}
                    style={{ width: 160 }}
                  />
                </FormGroup>

                {availableLanguageOptions.length > 1 ? (
                  <FormGroup
                    htmlFor="language-version"
                    label={messages.commonLanguage}
                    style={{ marginTop: 0 }}
                  >
                    <Select
                      field="languageVersion"
                      id="language-version"
                      options={availableLanguageOptions}
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                ) : null}
              </div>

              <FormGroup
                htmlFor={`comments-${participant.id}`}
                label={`${messages.commonComments} (${messages.commonOptional})`}
                maxWidth="100%"
              >
                <TextArea
                  autoGrow={true}
                  field="comments"
                  id={`comments-${participant.id}`}
                  placeholder={messages.commonAddComment}
                />
              </FormGroup>

              <FormGroup>
                <Checkbox
                  id="send-ceritificate-email"
                  field="sendCertificate"
                  label={messages.completionsSendCertificateToUser}
                />
              </FormGroup>
            </>
          )}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default FollowUsersToggleComplete;
